// Loading Components - Cosmic Theme
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

// Main cosmic loader
export const CosmicLoader = ({ size = 'md', text = '' }) => {
    const sizes = {
        sm: 'w-8 h-8',
        md: 'w-12 h-12',
        lg: 'w-16 h-16'
    };

    return (
        <div className="flex flex-col items-center justify-center gap-4">
            <motion.div
                className={`${sizes[size]} relative`}
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
                <Sparkles className="w-full h-full text-amber-400" />
                <motion.div
                    className="absolute inset-0"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                >
                    <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-xl" />
                </motion.div>
            </motion.div>
            {text && (
                <motion.p
                    className="text-amber-200 text-lg"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    {text}
                </motion.p>
            )}
        </div>
    );
};

// Skeleton loader for content
export const Skeleton = ({ className = '', variant = 'text' }) => {
    const variants = {
        text: 'h-4 rounded',
        title: 'h-8 rounded-lg',
        avatar: 'w-12 h-12 rounded-full',
        card: 'h-32 rounded-xl',
        button: 'h-10 w-24 rounded-lg'
    };

    return (
        <div
            className={`
                bg-gradient-to-r from-white/5 via-white/10 to-white/5
                animate-shimmer bg-[length:200%_100%]
                ${variants[variant]}
                ${className}
            `}
        />
    );
};

// Card skeleton
export const CardSkeleton = () => (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-4">
            <Skeleton variant="avatar" />
            <div className="flex-1 space-y-2">
                <Skeleton variant="text" className="w-3/4" />
                <Skeleton variant="text" className="w-1/2" />
            </div>
        </div>
    </div>
);

// Full page loader
export const PageLoader = ({ text = "Awakening the spirits..." }) => (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <CosmicLoader size="lg" text={text} />
    </div>
);

// Inline spinner
export const Spinner = ({ size = 'sm', className = '' }) => {
    const sizes = {
        xs: 'w-4 h-4',
        sm: 'w-5 h-5',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
    };

    return (
        <svg
            className={`animate-spin ${sizes[size]} ${className}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
};

export default CosmicLoader;
